// 세리사무소 포털 — 화면이 읽는 데이터.
//
// 전부 시안(세무대리인포털_메인_v8.html)에서 옮겨 온 목업이다. 실제 API 가 없어서 그렇다:
// 수임기업 목록·업무 큐·승인 처리·수익 기회·감사 로그를 주는 엔드포인트가 아직 없다
// (게이트웨이에 있는 것은 AI 대화(chat-api)·색인(embed-api)·FI 회계뿐).
//
// 그래서 지어낸 값이 화면 코드에 흩어지지 않도록 이 파일 하나로 모았다. 서버가 생기면
// 이 파일만 fetch 로 바뀌고 화면(Home.zeirishi.work / .revenue)은 그대로 둘 수 있다.

/** 수임기업 — ab 는 타일에 쓰는 2글자 약칭 */
export interface Company {
  id: string;
  nm: string;
  ab: string;
  biz: string;
  /** 본인 회사(세무법인 자신의 장부) — 책임 주체가 달라 구분한다 */
  own?: boolean;
  /** 확인 필요 업무 수 — 목록의 붉은 배지 */
  todo: number;
  /** 기장 진행률(%) */
  prog: number;
  /** 미처리 증빙 건수 */
  evi: number;
  /** 가장 급한 마감 한 줄 */
  due: string;
  /** 마감의 급함 — d 빨강 · w 주황 · 빈 값은 표시 없음 */
  dueLv: 'd' | 'w' | '';
  /** 신규 수임 */
  nw?: boolean;
}

/** 기한의 급함 — dq 빨강(임박) / dy 주황(주의) / dn 회색(여유) */
export type DueLevel = 'dq' | 'dy' | 'dn';

/** 판단 대기 업무 한 건 — '확인 필요' 탭에 쌓이는 것 */
export interface WorkItem {
  id: string;
  /** 목록 한 줄에 붙는 쟁점 — 클릭 전에 무엇을 판단할지 알 수 있게 한다 */
  pt: string;
  co: string;
  cat: string;
  title: string;
  due: string;
  dueLv: DueLevel;
  /** AI 가 확인을 요청한 이유 — <b> 강조가 들어간 HTML */
  ask: string;
  secN: string;
  secT: string;
  /** 판단 근거 표 — HTML. 고정 목업 문자열이라 그대로 싣는다(서버 값이 아니다) */
  key: string;
  /** 접어 두는 '자동 처리 내역' — HTML, 없으면 빈 문자열 */
  more: string;
  ok: string;
  okMsg: string;
  /** 수익 기회일 때만 — 예상 수수료(원) */
  fee?: number;
  /** 수익 기회일 때만 — 카테고리 묶음 */
  grp?: '공공' | '금융';
}

/** AI 가 사람 확인 없이 끝낸 건 — '진행한 업무'와 달리 승인자가 없다 */
export interface AutoItem {
  id: string;
  tm: string;
  who: string;
  cat: string;
  title: string;
  md: string;
  /** [시각, 처리, 결과] */
  log: [string, string, string][];
}

/* 본인 회사가 항상 맨 위다 — 세무대리인도 자기 법인의 장부를 스스로 기장하고,
   책임 주체가 다르기 때문에 수임기업과 섞어 두지 않는다(기획서 7.2). */
export const COMPANIES: Company[] = [
  { id: 'jd', nm: '세무법인 정담', ab: '정담', biz: '세무·회계 서비스', own: true, todo: 1, prog: 96, evi: 3, due: '법인세 중간예납 D-12', dueLv: 'w' },
  { id: 'hb', nm: '(주)한빛테크', ab: '한빛', biz: '제조·IT', todo: 2, prog: 92, evi: 12, due: '부가세 D-2', dueLv: 'd' },
  { id: 'ds', nm: '대성물류(주)', ab: '대성', biz: '운수·물류', todo: 1, prog: 88, evi: 4, due: '급여 D-3', dueLv: 'w' },
  { id: 'mr', nm: '미래건설(주)', ab: '미래', biz: '건설', todo: 1, prog: 76, evi: 23, due: '원천세 D-5', dueLv: 'w' },
  { id: 'gf', nm: '(주)그린푸드', ab: '그린', biz: '식품 제조', todo: 1, prog: 95, evi: 12, due: '—', dueLv: '' },
  { id: 'sr', nm: '상록수산업(주)', ab: '상록', biz: '기계 제조', todo: 1, prog: 81, evi: 7, due: '—', dueLv: '' },
  { id: 'wb', nm: '(주)웰스바이오', ab: '웰스', biz: '바이오', todo: 0, prog: 12, evi: 0, due: '신규 수임', dueLv: 'w', nw: true },
  { id: 'hs', nm: '한성전자(주)', ab: '한성', biz: '전자부품', todo: 0, prog: 100, evi: 0, due: '마감 완료', dueLv: '' },
  { id: 'jm', nm: '정민상사(주)', ab: '정민', biz: '도소매', todo: 0, prog: 100, evi: 0, due: '마감 완료', dueLv: '' },
];

/** 모르는 id 여도 화면이 멈추지 않게 — 목업이라 데이터가 어긋날 수 있다 */
export function companyOf(id: string): Company {
  const found = COMPANIES.filter(c => c.id === id)[0];
  return found || { id, nm: id, ab: id.slice(0, 2), biz: '', todo: 0, prog: 0, evi: 0, due: '', dueLv: '' };
}

/* ══ 확인 필요 업무 7건 ══ */
export const WORK_ITEMS: WorkItem[] = [
  {
    id: 'r1', pt: '불공제 판단 3건', co: 'hb', cat: '세무', title: '2026년 2기 부가세 예정신고', due: 'D-2', dueLv: 'dq',
    ask: 'AI가 신고서 초안까지 마쳤습니다. 매입 <b>3건</b>이 접대성 경비·비영업용 차량으로 판단되어 불공제 처리했습니다. 사업 관련성만 확인해 주세요.',
    secN: '3건', secT: '불공제 처리한 매입',
    key: `<table class="d"><tr><th>일자</th><th>거래처</th><th>적요</th><th class="r">공급가액</th><th class="r">세액</th></tr>
      <tr><td>08-14</td><td>미성한정식</td><td>거래처 접대 <span class="flag">접대비</span></td><td class="r num">2,400,000</td><td class="r num neg">240,000</td></tr>
      <tr><td>08-22</td><td>그랜드호텔</td><td>임원 워크숍 <span class="flag">접대비</span></td><td class="r num">5,800,000</td><td class="r num neg">580,000</td></tr>
      <tr><td>09-02</td><td>SK주유소 성수점</td><td>대표이사 차량 주유 <span class="flag">비영업용</span></td><td class="r num">4,200,000</td><td class="r num neg">420,000</td></tr></table>`,
    more: `<div><div class="sec">신고서 요약</div><div class="kv4">
      <div class="b"><div class="k">매출세액</div><div class="v">48,620,000</div></div>
      <div class="b"><div class="k">매입세액</div><div class="v">31,455,000</div></div>
      <div class="b"><div class="k">불공제</div><div class="v">1,240,000</div></div>
      <div class="b hi"><div class="k">납부예정세액</div><div class="v">17,165,000</div></div></div></div>
      <div><div class="sec">AI 자동 검증</div><div class="chks">
      <div class="chk"><span class="ok">✓</span>홈택스 전자세금계산서 대사<span class="rr">매출 142 / 매입 208건 일치</span></div>
      <div class="chk"><span class="ok">✓</span>신용카드 매입 자료 대사<span class="rr">86건 일치</span></div>
      <div class="chk"><span class="ok no">!</span>매입 사업관련성 판단<span class="rr">3건 확인 필요</span></div></div></div>`,
    ok: '승인 · 신고서 확정', okMsg: '한빛테크 부가세 신고서를 확정했습니다. 전자신고 대기열에 넣었습니다.',
  },
  {
    id: 'r2', pt: '연차수당 1건', co: 'ds', cat: 'HR', title: '9월 급여대장 확정', due: 'D-3', dueLv: 'dy',
    ask: '건강보험 요율 인상(7.09%→7.21%)을 전 직원에 반영했습니다. 중도 퇴사자 <b>박○○ 사원</b>의 잔여 연차 5.5일 수당만 취업규칙 해석이 필요합니다.',
    secN: '2건', secT: '확인이 필요한 사원',
    key: `<table class="d"><tr><th>사원</th><th>구분</th><th>내용</th><th class="r">금액</th></tr>
      <tr><td>박○○ (물류1팀)</td><td>중도 퇴사</td><td>잔여 연차 5.5일 수당 <span class="flag">판단 필요</span></td><td class="r num">1,320,000</td></tr>
      <tr><td>이○○ (관리팀)</td><td>신규 입사</td><td>9/8 입사 · 일할 계산 (23일)</td><td class="r num">2,760,000</td></tr></table>`,
    more: `<div><div class="sec">급여 요약</div><div class="kv4">
      <div class="b"><div class="k">대상 인원</div><div class="v">38명</div></div>
      <div class="b"><div class="k">지급 총액</div><div class="v">184,520,000</div></div>
      <div class="b"><div class="k">공제 총액</div><div class="v">28,340,000</div></div>
      <div class="b hi"><div class="k">실지급액</div><div class="v">156,180,000</div></div></div></div>`,
    ok: '승인 · 급여대장 확정', okMsg: '대성물류 급여대장을 확정했습니다. 이체 파일과 명세서 발송이 예약되었습니다.',
  },
  {
    id: 'r3', pt: '한도 초과 4건', co: 'mr', cat: '세무', title: '9월 원천세 · 일용직 지급명세서', due: 'D-5', dueLv: 'dy',
    ask: '현장 6곳 일용직 <b>142명</b>의 지급명세서를 자동 작성했습니다. 1일 15만원 비과세 한도를 넘는 <b>4건</b>만 확인해 주세요.',
    secN: '4건', secT: '비과세 한도 초과 지급',
    key: `<table class="d"><tr><th>현장</th><th>성명</th><th class="r">근로일수</th><th class="r">1일 평균</th><th class="r">지급액</th></tr>
      <tr><td>동탄 물류센터</td><td>김○○</td><td class="r num">12일</td><td class="r num neg">182,000</td><td class="r num">2,184,000</td></tr>
      <tr><td>동탄 물류센터</td><td>정○○</td><td class="r num">9일</td><td class="r num neg">168,000</td><td class="r num">1,512,000</td></tr>
      <tr><td>평택 오피스</td><td>최○○</td><td class="r num">15일</td><td class="r num neg">175,000</td><td class="r num">2,625,000</td></tr>
      <tr><td>청주 공장동</td><td>윤○○</td><td class="r num">7일</td><td class="r num neg">160,000</td><td class="r num">1,120,000</td></tr></table>`,
    more: `<div><div class="sec">AI 자동 처리</div><div class="chks">
      <div class="chk"><span class="ok">✓</span>출역일보 → 지급명세서 생성<span class="rr">142명</span></div>
      <div class="chk"><span class="ok">✓</span>하도급 지급확인 시스템 대사<span class="rr">6개 현장 일치</span></div>
      <div class="chk"><span class="ok no">!</span>비과세 한도 검증<span class="rr">4건 확인 필요</span></div></div></div>`,
    ok: '승인 · 신고서 확정', okMsg: '미래건설 원천세 신고서와 지급명세서를 확정했습니다.',
  },
  {
    id: 'r4', pt: '증빙 미확보 4건', co: 'gf', cat: '회계', title: '법인카드 증빙 미수취 12건', due: 'D-7', dueLv: 'dn',
    ask: '카드 매입 12건 중 <b>8건</b>은 영수증 OCR로 자동 매칭했습니다. 남은 <b>4건</b>은 증빙이 없어 계정 판단이 필요합니다.',
    secN: '4건', secT: '증빙 미확보',
    key: `<table class="d"><tr><th>일자</th><th>가맹점</th><th>사용자</th><th class="r">금액</th><th>AI 추정 계정</th></tr>
      <tr><td>09-03</td><td>스타벅스 역삼점</td><td>영업팀</td><td class="r num">184,000</td><td>복리후생비</td></tr>
      <tr><td>09-05</td><td>쿠팡</td><td>관리팀</td><td class="r num">427,500</td><td>소모품비</td></tr>
      <tr><td>09-09</td><td>대한항공</td><td>대표이사</td><td class="r num">1,240,000</td><td>여비교통비</td></tr>
      <tr><td>09-11</td><td>더현대 서울</td><td>대표이사</td><td class="r num">890,000</td><td>가지급금 <span class="flag">주의</span></td></tr></table>`,
    more: `<div><div class="sec">AI 자동 매칭 결과</div><div class="chks">
      <div class="chk"><span class="ok">✓</span>이메일 영수증 OCR 매칭<span class="rr">5건 · 2,340,000원</span></div>
      <div class="chk"><span class="ok">✓</span>홈택스 현금영수증 대사<span class="rr">3건 · 680,000원</span></div>
      <div class="chk"><span class="ok no">!</span>증빙 미확보<span class="rr">4건 · 2,741,500원</span></div></div></div>`,
    ok: 'AI 추정대로 전표 반영', okMsg: '그린푸드 법인카드 12건을 전표에 반영하고 증빙 요청 메일을 보냈습니다.',
  },
  {
    id: 'r5', pt: '이상 분개 2건', co: 'sr', cat: '회계', title: '자동 분개 이상 감지 2건', due: 'D-7', dueLv: 'dn',
    ask: '과거 24개월 동안 <b>한 번도 없던 규모</b>의 분개 2건을 감지했습니다. 비용이 아니라 자산으로 잡아야 할 가능성이 있습니다.',
    secN: '2건', secT: '이상 분개',
    key: `<table class="d"><tr><th>일자</th><th>적요</th><th>지정 계정</th><th class="r">금액</th><th>AI 의견</th></tr>
      <tr><td>09-07</td><td>(주)동방기계 설비 대금</td><td>수선비</td><td class="r num">62,000,000</td><td>기계장치(자산)</td></tr>
      <tr><td>09-12</td><td>컨설팅 용역료</td><td>지급수수료</td><td class="r num">28,000,000</td><td>개발비 자산화</td></tr></table>`,
    more: `<div><div class="sec">판단 근거</div><div class="chks">
      <div class="chk"><span class="ok no">!</span>수선비 월평균 420만원 → 이번 건 6,200만원<span class="rr">14.8배 · 24개월 내 최대</span></div>
      <div class="chk"><span class="ok">✓</span>거래처 업종 조회<span class="rr">동방기계 = 산업용 기계 제조</span></div>
      <div class="chk"><span class="ok">✓</span>세금계산서 품목<span class="rr">"CNC 선반 1식" 기재</span></div></div></div>`,
    ok: '자산 계정으로 재분개', okMsg: '상록수산업 전표 2건을 자산 계정으로 재분개했습니다.',
  },
  {
    id: 'r6', pt: '사업계획 확인', co: 'hb', cat: '공공', title: '정책자금 신청서 초안', due: 'D-9', dueLv: 'dn',
    ask: '중진공 혁신성장자금 5억 신청서의 <b>정량 항목 전체를 기장 데이터로 자동 기입</b>했습니다. 사업계획 요약 500자만 확인하면 제출 가능합니다.',
    secN: '5개 항목', secT: '자동 기입된 정량 요건',
    key: `<table class="d"><tr><th>항목</th><th class="r">값</th><th>출처</th><th>요건</th></tr>
      <tr><td>최근 3년 평균 매출</td><td class="r num">42.8억</td><td>재무제표</td><td>충족 (100억 이하)</td></tr>
      <tr><td>매출 증가율</td><td class="r num">+31.2%</td><td>부가세 신고</td><td>충족 (20% 이상)</td></tr>
      <tr><td>상시 근로자 수</td><td class="r num">34명</td><td>4대보험</td><td>충족</td></tr>
      <tr><td>부채비율</td><td class="r num">118%</td><td>재무제표</td><td>충족 (200% 이하)</td></tr>
      <tr><td>연구개발비 비중</td><td class="r num">4.1%</td><td>손익계산서</td><td>가점 대상</td></tr></table>`,
    more: `<div><div class="sec">예상 성과</div><div class="kv4">
      <div class="b"><div class="k">신청 자금</div><div class="v">5억원</div></div>
      <div class="b"><div class="k">금리</div><div class="v">2.9%</div></div>
      <div class="b"><div class="k">통과 확률</div><div class="v">78%</div></div>
      <div class="b hi"><div class="k">예상 수수료</div><div class="v">2,500,000</div></div></div></div>`,
    ok: '초안 승인 · 고객사 확인 요청', okMsg: '한빛테크에 신청서 초안을 발송했습니다. 성사 시 수수료 250만원이 예상됩니다.',
  },
  {
    id: 'r7', pt: '납부방식 결정', co: 'jd', cat: '세무', title: '법인세 중간예납 세액 검토 (우리 사무소)', due: 'D-12', dueLv: 'dn',
    ask: '우리 사무소 법인세 중간예납을 두 가지 방식으로 계산했습니다. <b>직전연도 실적 기준</b>과 <b>가결산 기준</b>의 차이가 <b>5,660,000원</b>입니다. 어느 쪽으로 납부할지 결정해 주세요.',
    secN: '2가지', secT: '중간예납 계산 방식 비교',
    key: `<table class="d"><tr><th>구분</th><th>계산 근거</th><th class="r">납부세액</th><th>비고</th></tr>
      <tr><td>직전연도 실적 기준</td><td>2025년 산출세액 36,840,000 × 1/2</td><td class="r num">18,420,000</td><td>신고서 작성 불필요</td></tr>
      <tr class="warn"><td>가결산 기준</td><td>2026년 상반기 실적 가결산</td><td class="r num">12,760,000</td><td>중간예납 신고서 필요 <span class="flag">유리</span></td></tr></table>`,
    more: `<div><div class="sec">AI 자동 검증</div><div class="chks">
      <div class="chk"><span class="ok">✓</span>상반기 수임료 매출 집계<span class="rr">1,284건 · 486,200,000원</span></div>
      <div class="chk"><span class="ok">✓</span>인건비·임차료 등 판관비 반영<span class="rr">312,400,000원</span></div>
      <div class="chk"><span class="ok no">!</span>납부 방식 선택<span class="rr">결정 필요</span></div></div></div>`,
    ok: '가결산 기준으로 확정', okMsg: '세무법인 정담 법인세 중간예납을 가결산 기준(12,760,000원)으로 확정했습니다. 5,660,000원을 절감합니다.',
  },
];

/* ══ 수익 기회 4건 ══
   업무와 같은 실행 패널에서 열리므로 같은 형태(WorkItem)로 둔다 —
   기획서의 "카드를 누르면 같은 실행 패널에서 열린다 · 화면을 떠나지 않는다"가 이것이다. */
export const OPPORTUNITIES: WorkItem[] = [
  {
    id: 'o1', grp: '공공', co: 'hb', cat: '공공 · 정책자금', title: '중진공 혁신성장자금 5억', fee: 2500000,
    due: '접수 D-9', dueLv: 'dy', pt: '제안 대기',
    ask: '매출 증가율 31.2% · 부채비율 118% · R&D 비중 4.1%로 <b>우대 요건 3개를 충족</b>합니다. 심사 통과 확률은 78%로 추정됩니다.',
    secN: '', secT: '제안 조건',
    key: `<div class="kv4"><div class="b"><div class="k">한도</div><div class="v">5억원</div></div><div class="b"><div class="k">금리</div><div class="v">2.9%</div></div><div class="b"><div class="k">통과 확률</div><div class="v">78%</div></div><div class="b hi"><div class="k">예상 수수료</div><div class="v">2,500,000</div></div></div>`,
    more: `<div><div class="sec">발송 내용</div><div class="chks">
      <div class="chk"><span class="ok">✓</span>세무법인 정담 명의 제안서<span class="rr">자동 생성</span></div>
      <div class="chk"><span class="ok">✓</span>신청서 초안 · 첨부서류 9종<span class="rr">준비 완료</span></div></div></div>`,
    ok: '고객사에 제안서 발송', okMsg: '한빛테크에 정책자금 제안서를 발송했습니다. 성사 시 법인 몫 175만원입니다.',
  },
  {
    id: 'o2', grp: '금융', co: 'gf', cat: '금융 · 운전자금', title: '운전자금 대출 3억', fee: 1500000,
    due: '제안 가능', dueLv: 'dn', pt: '제안 대기',
    ask: '매출이 전년 대비 <b>+41%</b> 성장하며 운전자금 수요가 발생했습니다. 제휴 금융기관 3곳 사전 조회 결과 최대 3.2억 · 최저 금리 4.8%입니다.',
    secN: '3곳', secT: '사전 한도 조회 결과',
    key: `<table class="d"><tr><th>금융기관</th><th class="r">한도</th><th class="r">금리</th><th>비고</th></tr>
      <tr><td>A은행</td><td class="r num">320,000,000</td><td class="r num">4.8%</td><td>신용보증 연계</td></tr>
      <tr><td>B은행</td><td class="r num">280,000,000</td><td class="r num">5.1%</td><td>—</td></tr>
      <tr><td>C캐피탈</td><td class="r num">300,000,000</td><td class="r num">6.4%</td><td>즉시 실행</td></tr></table>`,
    more: '', ok: '고객사에 제안서 발송', okMsg: '그린푸드에 대출 제안서를 발송했습니다.',
  },
  {
    id: 'o3', grp: '금융', co: 'sr', cat: '금융 · 매출채권', title: '매출채권 팩토링 8.2억', fee: 820000,
    due: '긴급 제안', dueLv: 'dq', pt: '제안 대기',
    ask: '신용등급이 BB+에서 BB로 하락해 차입 여력이 줄었습니다. 우량 채권 <b>6.4억</b>을 즉시 현금화하면 부채비율도 24%p 개선됩니다.',
    secN: '3건', secT: '보유 매출채권 분석',
    key: `<table class="d"><tr><th>거래처</th><th class="r">채권액</th><th class="r">경과일</th><th>등급</th></tr>
      <tr><td>(주)대한중공업</td><td class="r num">380,000,000</td><td class="r num">64일</td><td>A</td></tr>
      <tr><td>동방기계(주)</td><td class="r num">260,000,000</td><td class="r num">72일</td><td>A-</td></tr>
      <tr><td>세일산업</td><td class="r num">180,000,000</td><td class="r num neg">128일</td><td>B <span class="flag">제외</span></td></tr></table>`,
    more: '', ok: '고객사에 제안서 발송', okMsg: '상록수산업에 팩토링 제안서를 발송했습니다.',
  },
  {
    id: 'o4', grp: '공공', co: 'ds', cat: '공공 · 개인 (직원)', title: '노란우산공제 · 근로자 지원금', fee: 400000,
    due: '상시', dueLv: 'dn', pt: '제안 대기',
    ask: '수임기업 <b>직원 개인</b>이 받을 수 있는 혜택입니다. 세무법인 명의로 단체 설명회를 열면 가입률이 평균 3배 높아집니다.',
    secN: '2개 제도', secT: '대상 직원 분석',
    key: `<table class="d"><tr><th>제도</th><th class="r">대상</th><th>혜택</th><th class="r">건당 수수료</th></tr>
      <tr><td>노란우산공제</td><td class="r num">8명</td><td>연 최대 500만원 소득공제</td><td class="r num">40,000</td></tr>
      <tr><td>청년내일채움공제</td><td class="r num">4명</td><td>2년 만기 1,200만원</td><td class="r num">20,000</td></tr></table>`,
    more: '', ok: '안내 자료 발송', okMsg: '대성물류 직원 12명에게 안내 자료를 발송했습니다.',
  },
];

/* ══ AI 가 밤사이 끝낸 일 ══ */
export const AUTO_ITEMS: AutoItem[] = [
  {
    id: 'a1', tm: '03:08', who: '148개사', cat: '회계', title: '전자세금계산서 자동 수취 · 분개',
    md: '1,842건 수취 · 자동분개 1,827건 · 정확도 99.2% · 오류 0건',
    log: [['02:00 ~ 02:14', '홈택스 API 수집', '1,842건'], ['02:14 ~ 03:02', '계정 추정 · 전표 생성', '1,827건'], ['03:02 ~ 03:08', '중복·오류 검증', '보류 15건']],
  },
  {
    id: 'a2', tm: '03:22', who: '148개사', cat: 'HR', title: '4대보험 요율 변경 일괄 반영',
    md: '3,284명 · 월 38,400,000원 증가 · 실패 0건',
    log: [['03:15', '건강보험 요율 7.09% → 7.21%', '고시 반영'], ['03:15 ~ 03:22', '급여 마스터 일괄 갱신', '148개사 3,284명']],
  },
  {
    id: 'a3', tm: '04:10', who: '(주)한빛테크', cat: '세무', title: '부가세 예정신고서 초안 작성',
    md: '매출 142건 · 매입 208건 대사 완료 · 불공제 3건 보류',
    log: [['04:02', '전자세금계산서 대사', '매출 142 / 매입 208'], ['04:06', '신용카드 매입 대사', '86건 일치'], ['04:10', '신고서 초안 생성', '불공제 3건 보류']],
  },
  {
    id: 'a4', tm: '04:35', who: '대성물류(주)', cat: 'HR', title: '9월 급여 계산 · 명세서 생성',
    md: '38명 · 연장·야간수당 21명 자동 반영',
    log: [['04:28', '근태 연동 · 수당 계산', '21명'], ['04:32', '간이세액표 원천징수', '38명'], ['04:35', '명세서 생성', '38건']],
  },
  {
    id: 'a5', tm: '05:02', who: '미래건설(주)', cat: '세무', title: '일용직 지급명세서 자동 작성',
    md: '6개 현장 · 142명 · 하도급 시스템 대사 완료',
    log: [['04:50', '출역일보 수집', '6개 현장'], ['04:58', '지급명세서 생성', '142명'], ['05:02', '비과세 한도 검증', '초과 4건 보류']],
  },
  {
    id: 'a6', tm: '05:40', who: '(주)그린푸드', cat: '회계', title: '법인카드 영수증 OCR 매칭',
    md: '12건 중 8건 자동 매칭 · 3,020,000원',
    log: [['05:31', '이메일 영수증 OCR', '5건 · 2,340,000원'], ['05:36', '현금영수증 대사', '3건 · 680,000원'], ['05:40', '미확보 분류', '4건 보류']],
  },
  {
    id: 'a7', tm: '06:15', who: '148개사', cat: '공공', title: '정책자금 공고 매칭 스캔',
    md: '신규 공고 3건 · 요건 충족 7개사 도출',
    log: [['06:05', '공고 수집', '중기부·지자체 3건'], ['06:15', '재무·인력 요건 대조', '148개사 → 7개사']],
  },
  {
    id: 'a8', tm: '06:48', who: '상록수산업(주)', cat: '회계', title: '전표 이상 패턴 검사',
    md: '24개월 학습 기준 · 이상 2건 감지',
    log: [['06:40', '계정별 분포 학습', '24개월'], ['06:48', '이상치 판정', '2건 검토 승격']],
  },
];

/** 목록에 개별로 싣지 않은 나머지 자동 처리 건수 */
export const AUTO_ETC = 16;
/** 브리핑·탭에 쓰는 오늘의 AI 자동 처리 총건수 */
export const AUTO_TOTAL = 24;

/* ══ 이번 달 성과 ══ */
export const MONTHLY = {
  total: 12480000,
  note: '▲ 전월 대비 +38% · 성사 11건',
  bars: [
    { label: '공공 · 정책자금', amount: 6200000, pct: 50, color: '#af52de' },
    { label: '금융 · 대출 주선', amount: 3900000, pct: 31, color: '#0071e3' },
    { label: '보험 · 팩토링', amount: 1680000, pct: 13, color: '#34c759' },
    { label: '커머스 · MRO', amount: 700000, pct: 6, color: '#ff9500' },
  ],
};

/** 수임기업 관련 변동 — warn 이면 주황 칩 */
export interface Alert {
  mark: string;
  warn?: boolean;
  text: string;
  when: string;
}

export const ALERTS: Alert[] = [
  { mark: '▼', warn: true, text: '상록수산업(주) 신용등급 BB+ → BB 하락 — 팩토링 제안 시점', when: '32분 전' },
  { mark: '公', text: '중기부 「2026 스마트공장 고도화」 공고 — 수임기업 7개사 요건 충족', when: '2시간 전' },
  { mark: '業', text: '건설업 표준품셈 개정(10월 적용) — 건설 수임 5개사 원가 검토', when: '4시간 전' },
  { mark: '◎', text: '(주)그린푸드 매출 전년 동월 +41% — 운전자금 수요 예상', when: '어제' },
  { mark: '保', warn: true, text: '대성물류(주) 직원 12명 노란우산공제 미가입', when: '어제' },
];

/* ══ AI 와 했던 일 ══
   검색바의 시계 버튼에서 펼친다. 대화는 실제 게이트웨이에 있지만 그 이력을 목록으로 주는
   API 가 아직 없어 목업이다. done=false 는 아직 끝내지 않은 일 — 이어서 할 것이 남았다. */
export interface AiHistoryItem {
  tm: string;
  /** 그 대화가 어느 수임기업 기준이었나 — 빈 값이면 사무소 전체 */
  co: string;
  q: string;
  a: string;
  done: boolean;
  /** 남은 일 한 줄 — done=false 일 때만 */
  note?: string;
}

export const AI_HISTORY: AiHistoryItem[] = [
  {
    tm: '09:41', co: 'hb', q: '불공제 3건, 판단 근거 정리해줘',
    a: '접대비 2건 · 비영업용 차량 1건으로 정리했습니다.',
    done: false, note: '운행기록부 확인 메일 초안까지 썼고, 발송 전입니다.',
  },
  {
    tm: '09:12', co: 'ds', q: '9월 급여 이체 파일 만들어줘',
    a: '38명 · 156,180,000원 이체 파일을 생성했습니다.', done: true,
  },
  {
    tm: '08:55', co: '', q: '정책자금 요건 충족 기업 찾아줘',
    a: '148개사 중 7개사를 도출했습니다.',
    done: false, note: '제안서는 한빛테크 1건만 보냈습니다.',
  },
  {
    tm: '어제 17:20', co: 'gf', q: '운전자금 대출 한도 조회해줘',
    a: 'A은행 3.2억(4.8%) 등 3곳 사전 한도를 받았습니다.',
    done: false, note: '고객사 제안서 발송 대기 중입니다.',
  },
  {
    tm: '어제 14:05', co: 'mr', q: '현장별 노무비 배부 기준 설명해줘',
    a: '직접노무비 기준 배부표를 정리해 드렸습니다.', done: true,
  },
];

/** 금액 표기 — 화면 전체가 같은 구분자를 쓰도록 한 곳에 둔다 */
export function won(n: number): string {
  return n.toLocaleString('ko-KR');
}
